import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  ArrowsLeftRight,
  FileText,
  CheckCircle,
  WarningCircle,
  CurrencyDollar,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct, qtd, notaTipoFromLabel } from '../format';
import {
  IAuditoriaEmpresa,
  IIntegracao,
  IIntegracaoPendente,
  IIntegracaoDivergencia,
  NotaTipo,
} from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { EmptyState } from '../components/EmptyState';
import { NoteDrawer } from './NoteDrawer';
import { UI } from '../ui';

interface IntegracaoScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Cruzamentos: navegar a outro submódulo (Contábil, Notas...). */
  onScreen: (screen: string) => void;
}

const GLASS = {
  borderRadius: '16px',
  overflow: 'hidden',
  bg: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(14px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.85)',
  boxShadow: '0 12px 34px -18px rgba(15,23,42,0.35)',
} as const;

export function IntegracaoScreen({ empresa, periodo, onScreen }: IntegracaoScreenProps) {
  const { getIntegracao, getIntegracaoPendentes, getIntegracaoDivergencias } =
    useAuditoriaInteligente();
  const [drawer, setDrawer] = useState<{ codigo: string; tipo: NotaTipo } | null>(null);

  const { data, isFetching } = useQuery<IIntegracao>({
    queryKey: ['ai-integracao', { id: empresa?.id, ...periodo }],
    queryFn: getIntegracao,
    enabled: !!empresa,
  });

  const { data: pendentes } = useQuery<IIntegracaoPendente[]>({
    queryKey: ['ai-integracao-pendentes', { id: empresa?.id, limit: 20, ...periodo }],
    queryFn: getIntegracaoPendentes,
    enabled: !!empresa,
  });

  const { data: divergencias } = useQuery<IIntegracaoDivergencia[]>({
    queryKey: ['ai-integracao-divergencias', { id: empresa?.id, limit: 20, ...periodo }],
    queryFn: getIntegracaoDivergencias,
    enabled: !!empresa,
  });

  // Coerção decimal-string → número, senão a área do gráfico não desenha.
  const evolucao = useMemo(
    () => (data?.evolucao ?? []).map(e => ({ ...e, pct_integrado: num(e.pct_integrado) })),
    [data],
  );
  const valorPendente = useMemo(
    () => (data?.por_tipo ?? []).reduce((s, t) => s + num(t.valor_pendente), 0),
    [data],
  );

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver a integração fiscal-contábil."
        icon={ArrowsLeftRight}
      />
    );
  }

  if (isFetching && !data) {
    return (
      <Box textAlign="center" py={16}>
        <Spinner />
      </Box>
    );
  }

  const r = data?.resumo;

  return (
    <Box>
      <Grid
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' }}
        gap={4}
        mb={5}
      >
        <KpiCard
          label="Total notas"
          value={qtd(r?.total_notas)}
          icon={FileText}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          onClick={() => onScreen('notas')}
          info={
            <InfoBloco
              oque="Total de notas fiscais da empresa no período, base do cruzamento fiscal × contábil."
              dados="COUNT(*) em extract_integracao_fiscal_contabil"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Integradas"
          value={qtd(r?.integradas)}
          icon={CheckCircle}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          onClick={() => onScreen('contabil')}
          info={
            <InfoBloco
              oque="Notas que já possuem lançamento contábil (valor contábil maior que zero)."
              dados="COUNT(*) FILTER (WHERE valor > 0) em extract_integracao_fiscal_contabil"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Pendentes"
          value={qtd(r?.pendentes)}
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          info={
            <InfoBloco
              oque="Notas fiscais sem lançamento contábil correspondente (valor contábil zerado)."
              dados="COUNT(*) FILTER (WHERE valor = 0) em extract_integracao_fiscal_contabil"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="R$ pendente"
          value={formatCurrency(valorPendente)}
          sub="valor sem lançamento"
          icon={CurrencyDollar}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          info={
            <InfoBloco
              oque="Soma do valor contábil das notas pendentes, ou seja, sem lançamento contábil."
              dados="SUM(valor_contabil) FILTER (WHERE valor = 0) em extract_integracao_fiscal_contabil"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="% integrado"
          value={pct(r?.pct_integrado)}
          icon={ArrowsLeftRight}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          onClick={() => onScreen('contabil')}
          info={
            <InfoBloco
              oque="Percentual de notas integradas (com lançamento contábil) sobre o total de notas do período."
              dados="COUNT(*) FILTER (WHERE valor > 0) / COUNT(*) em extract_integracao_fiscal_contabil"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '3fr 2fr' }} gap={5} mb={5}>
        <Box
          bg={UI.glass}
          border="1px solid"
          borderColor={UI.glassBorder}
          borderRadius="16px"
          backdropFilter={UI.glassBlur}
          boxShadow={UI.glassShadow}
          p={5}
        >
          <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
            Evolução da integração (%)
          </Text>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={evolucao}>
              <defs>
                <linearGradient id="aiInteg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={UI.pos} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={UI.pos} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
              <YAxis
                fontSize={11}
                stroke={UI.muted}
                domain={[0, 100]}
                tickFormatter={v => `${v}%`}
              />
              <Tooltip formatter={(v: number) => pct(v)} />
              <Area
                type="monotone"
                dataKey="pct_integrado"
                name="% integrado"
                stroke={UI.pos}
                fill="url(#aiInteg)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        <Box {...GLASS}>
          <Box px={5} pt={4} pb={2}>
            <Text fontSize="13px" fontWeight={700} color={UI.ink}>
              Por tipo
            </Text>
          </Box>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Tipo</Th>
                  <Th isNumeric>Pendentes</Th>
                  <Th isNumeric>Valor pend.</Th>
                  <Th isNumeric>% integr.</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.por_tipo ?? []).map(t => (
                  <Tr key={t.tipo_nota} _hover={{ bg: UI.rowHover }}>
                    <Td fontWeight={700}>{t.tipo}</Td>
                    <Td isNumeric color={num(t.pendentes) ? UI.neg : UI.muted}>
                      {qtd(t.pendentes)}
                    </Td>
                    <Td isNumeric>{formatCurrency(t.valor_pendente)}</Td>
                    <Td isNumeric fontWeight={700}>
                      {pct(t.pct_integrado)}
                    </Td>
                  </Tr>
                ))}
                {(data?.por_tipo ?? []).length === 0 && (
                  <Tr>
                    <Td colSpan={4} textAlign="center" py={6} color={UI.muted}>
                      Sem dados.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>

      <Box {...GLASS} mb={5}>
        <Box px={5} pt={4} pb={2}>
          <Text fontSize="13px" fontWeight={700} color={UI.ink}>
            Notas pendentes de integração
          </Text>
        </Box>
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th>Documento</Th>
                <Th>Tipo</Th>
                <Th>Data</Th>
                <Th>Acumulador</Th>
                <Th isNumeric>Valor contábil</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(pendentes ?? []).map(p => (
                <Tr
                  key={p.codigo_nota}
                  cursor={notaTipoFromLabel(p.tipo_nota) ? 'pointer' : 'default'}
                  _hover={{ bg: UI.rowHover }}
                  onClick={() => {
                    const tipo = notaTipoFromLabel(p.tipo_nota);
                    if (tipo) setDrawer({ codigo: p.codigo_nota, tipo });
                  }}
                  title={notaTipoFromLabel(p.tipo_nota) ? 'Ver detalhes da nota' : undefined}
                >
                  <Td fontWeight={700}>{p.documento}</Td>
                  <Td>{p.tipo_nota}</Td>
                  <Td>{p.data_nota}</Td>
                  <Td maxW="220px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {p.acumulador_nome}
                  </Td>
                  <Td isNumeric>{formatCurrency(p.valor_contabil)}</Td>
                </Tr>
              ))}
              {(pendentes ?? []).length === 0 && (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={6} color={UI.muted}>
                    Nenhuma nota pendente.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Box {...GLASS}>
        <Box px={5} pt={4} pb={2}>
          <Text fontSize="13px" fontWeight={700} color={UI.ink}>
            Divergência de valor (fiscal × contábil)
          </Text>
        </Box>
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th>Documento</Th>
                <Th>Data</Th>
                <Th>Lançamento (D → C)</Th>
                <Th isNumeric>Valor contábil</Th>
                <Th isNumeric>Valor fiscal</Th>
                <Th isNumeric>Divergência</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(divergencias ?? []).map(d => (
                <Tr
                  key={d.codigo_nota}
                  cursor={notaTipoFromLabel(d.tipo_nota) ? 'pointer' : 'default'}
                  _hover={{ bg: UI.rowHover }}
                  onClick={() => {
                    const tipo = notaTipoFromLabel(d.tipo_nota);
                    if (tipo) setDrawer({ codigo: d.codigo_nota, tipo });
                  }}
                  title={notaTipoFromLabel(d.tipo_nota) ? 'Ver detalhes da nota' : undefined}
                >
                  <Td fontWeight={700}>{d.documento}</Td>
                  <Td>{d.data_nota}</Td>
                  <Td maxW="240px" fontSize="11.5px" color={UI.muted} whiteSpace="normal">
                    {d.descricao_debito || '—'} → {d.descricao_credito || '—'}
                  </Td>
                  <Td isNumeric>{formatCurrency(d.valor_contabil)}</Td>
                  <Td isNumeric>{formatCurrency(d.valor)}</Td>
                  <Td isNumeric fontWeight={700} color={UI.neg}>
                    {formatCurrency(d.divergencia)}
                  </Td>
                </Tr>
              ))}
              {(divergencias ?? []).length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={6} color={UI.muted}>
                    Nenhuma divergência de valor.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {drawer && empresa && (
        <NoteDrawer
          empresaId={empresa.id}
          tipo={drawer.tipo}
          codigo={drawer.codigo}
          isOpen={!!drawer}
          onClose={() => setDrawer(null)}
        />
      )}
    </Box>
  );
}
